import { faAdd, faCheck, faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { deleteDepartment, patchDepartment, postJob } from "../../../services/httpClient";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import IconButton from "../../ui/IconButton";
import JobCard from "./JobCard";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";

interface CardState {
  department: any;
  getJobs: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const DepartmentCard = ({ department, getJobs, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [departmentName, setDepartmentName] = useState<string>(department[0]?.department);
  const [showAddJob, setShowAddJob] = useState<boolean>(false);
  const [newJob, setNewJob] = useState<string>("");
  const [newPositionTotal, setNewPositionTotal] = useState<number>();

  const onClickAddJob = () => {
    setShowAddJob(true);
    setNewJob("");
    setNewPositionTotal(undefined);
  };

  const updateNewJob = (e: any) => {
    setNewJob(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateNewPositionTotal = (e: any) => {
    setNewPositionTotal(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickSaveJob = (isDone: boolean = false) => {
    const _job = {
      name: newJob,
      positions_total: newPositionTotal,
    };

    setErrMessage("");

    postJob(_job, department[0]?.department_id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Job Created");
        setToastBody(newJob);
        setNewJob("");
        setNewPositionTotal(undefined);
        unsavedChanges.value = false;
        setOrgToSave(false);
        if (isDone) {
          setShowAddJob(false);
        }
        getJobs(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelAddJob = () => {
    setErrMessage("");
    setShowAddJob(false);
    setNewJob("");
    setNewPositionTotal(undefined);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickEdit = () => {
    setShowAddJob(false);
    setIsEditing(true);
  };

  const updateDepartmentName = (e: any) => {
    setDepartmentName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickEditFinal = () => {
    const updatedDept = {
      name: departmentName,
    };

    patchDepartment(updatedDept, department[0]?.department_id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Department Updated");
        setToastBody(departmentName);
        setIsEditing(false);
        setErrMessage(undefined);
        getJobs(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteDepartment(department[0]?.department_id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Department Deleted");
        setToastBody(departmentName);
        setIsDeleting(false);
        setErrMessage(undefined);
        getJobs(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  const renderEdit = () => {
    return (
      <Row className="mb-2">
        <Stack direction="horizontal" gap={3}>
          <Col>
            <Input label="Department" type="text" value={departmentName} onUpdate={(e: any) => updateDepartmentName(e)} />
          </Col>
          <Stack className="ms-auto" direction="horizontal" gap={3}>
            <IconButton onClickButton={onClickEditFinal} tooltipText={"Save"} icon={faCheck} />
            <div className="vr" />
            <IconButton onClickButton={onClickCancelEdit} tooltipText={"Cancel"} icon={faClose} />
          </Stack>
        </Stack>
      </Row>
    );
  };

  const renderDelete = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Stack style={{ justifyContent: "center" }} gap={2}>
          <Card.Subtitle>
            Delete {department[0]?.department} and {department?.length} Jobs?
          </Card.Subtitle>
        </Stack>
        <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
          Confirm Delete
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
          Cancel
        </Button>
      </Stack>
    );
  };

  return (
    <Card key={department[0]?.department_id} className="bg-light p-3">
      <>
        {isDeleting ? (
          <>{renderDelete()}</>
        ) : (
          <>
            {isEditing ? (
              <>{renderEdit()}</>
            ) : (
              <Row>
                <Col xs={9}>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                    }}
                  >
                    <h6>{department[0]?.department}</h6>
                  </div>
                </Col>
                <Col xs={3} className="d-flex justify-content-end">
                  <Stack direction="horizontal" gap={3} className="mb-3">
                    <IconEmbeddedButton onClickButton={() => onClickAddJob()} tooltipText="Add Job" icon={faAdd} />
                    <div className="vr" />
                    <IconEmbeddedButton onClickButton={onClickEdit} tooltipText={"Edit Department"} icon={faEdit} />
                    <div className="vr" />
                    <IconEmbeddedButton onClickButton={onClickDelete} tooltipText={"Delete Department"} icon={faTrash} />
                  </Stack>
                </Col>
              </Row>
            )}
            {showAddJob && (
              <>
                <Row>
                  <Stack direction="horizontal" gap={3} className="mb-3">
                    <Col xs={9}>
                      <Input
                        label="Job"
                        type="text"
                        value={newJob}
                        onUpdate={(e: any) => updateNewJob(e)}
                        hasFocus={true}
                        required={true}
                      />
                    </Col>
                    <Col>
                      <Input
                        label="Positions #"
                        type="number"
                        value={newPositionTotal}
                        onUpdate={(e: any) => updateNewPositionTotal(e)}
                        required={true}
                      />
                    </Col>
                  </Stack>
                </Row>
                <Row>
                  <Stack direction="horizontal" gap={3} className="mb-3">
                    <Button
                      className="app-secondary-color button"
                      disabled={!newJob || !newPositionTotal}
                      onClick={() => onClickSaveJob(false)}
                    >
                      Save and Add Another
                    </Button>
                    <div className="vr" />
                    <Button
                      className="app-secondary-color button"
                      disabled={!newJob || !newPositionTotal}
                      onClick={() => onClickSaveJob(true)}
                    >
                      Save and Done
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickCancelAddJob()}>
                      Done
                    </Button>
                  </Stack>
                </Row>
              </>
            )}
            <Stack gap={2}>
              {department?.map((j: any, index: number) => (
                <JobCard
                  key={index}
                  job={j}
                  getJobs={getJobs}
                  setOrgToSave={setOrgToSave}
                  setErrMessage={setErrMessage}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              ))}
            </Stack>
          </>
        )}
      </>
    </Card>
  );
};

export default DepartmentCard;
